<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="lead-scoring-page">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-4">
            <div>
                <span class="pilot-label">PILOTO 2027 - 1 · {{ pregradoMode ? 'PREGRADO' : 'ADVANCE Y POSTGRADO' }}</span>
                <h2 class="mb-1 mt-2">{{ pregradoMode ? 'Ranking de participación' : 'Priorización de leads' }}</h2>
                <p class="text-muted mb-0">{{ pregradoMode ? `Participación registrada en eventos y simuladores UGM de ${eventYear}. Es un indicador descriptivo, no una probabilidad de matrícula.` : 'Score de interés observado más probabilidad histórica. No reemplaza la decisión del equipo.' }}</p>
            </div>
            <div class="mt-3 mt-md-0">
                <a :href="pregradoMode ? '/web/dashboard/leadscoring' : '/web/dashboard/leadscoringpregrado'" class="btn btn-outline-primary mr-2">
                    {{ pregradoMode ? 'Ver Advance/Postgrado' : 'Ver Pregrado' }}
                </a>
            </div>
        </div>

        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <div class="row mb-4">
            <div class="col-md-3 mb-3" v-for="card in cards" :key="card.label">
                <div class="metric-card h-100">
                    <small>{{ card.label }}</small>
                    <strong>{{ card.value }}</strong>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm">
            <div class="card-body">
                <div class="row align-items-end mb-3">
                    <div class="col-lg-4 col-md-6">
                        <label>Buscar lead, carrera, responsable o fuente</label>
                        <input v-model.trim="search" class="form-control" placeholder="Escriba para filtrar…">
                    </div>
                    <div class="col-lg-2 col-md-6 mt-3 mt-md-0">
                        <label>{{ pregradoMode ? 'Nivel de participación' : 'Nivel' }}</label>
                        <select v-model="level" class="form-control">
                            <option value="">Todos</option>
                            <option>Muy alto</option><option>Alto</option><option>Medio</option><option>Bajo</option>
                        </select>
                    </div>
                    <div v-if="!pregradoMode" class="col-lg-3 col-md-6 mt-3 mt-lg-0">
                        <label>Tipo de programa</label>
                        <select v-model="program" class="form-control">
                            <option value="">Todos los tipos</option>
                            <option v-for="option in programOptions" :key="option" :value="option">{{ option }}</option>
                        </select>
                    </div>
                    <div v-else class="col-lg-3 col-md-6 mt-3 mt-lg-0">
                        <label>Año de interacciones</label>
                        <select v-model.number="eventYear" class="form-control" @change="changeEventYear">
                            <option v-for="year in eventYearOptions" :key="year" :value="year">{{ year }}</option>
                        </select>
                    </div>
                    <div v-if="!pregradoMode" class="col-lg-3 col-md-6 mt-3 mt-lg-0">
                        <label>Responsable</label>
                        <select v-model="owner" class="form-control">
                            <option value="">Todos los responsables</option>
                            <option v-for="option in ownerOptions" :key="option" :value="option">{{ option }}</option>
                        </select>
                    </div>
                    <div class="col-12 mt-3 d-flex flex-wrap justify-content-between align-items-center text-muted">
                        <small>Mostrando {{ filteredLeads.length }} de {{ report.total || 0 }} {{ pregradoMode ? 'asistentes' : 'leads' }} · página {{ report.page || 1 }} de {{ report.last_page || 1 }}</small>
                        <small>Modelo {{ report.modelo_version || 'sin calcular' }} · {{ formattedDate }}</small>
                    </div>
                </div>

                <div v-if="loading" class="text-center py-5"><b-spinner label="Cargando"></b-spinner></div>
                <div v-else-if="!report.total" class="empty-state text-center py-5">
                    <i class="bx bx-bullseye"></i>
                    <h5>Aún no hay resultados</h5>
                    <p>{{ pregradoMode ? `No hay participantes de eventos o simuladores de ${eventYear} en este ambiente.` : 'Use “Recalcular piloto” para clasificar los leads del período 2027 - 1.' }}</p>
                </div>
                <div v-else class="table-responsive">
                    <table v-if="pregradoMode" class="table table-hover align-middle scoring-table">
                        <thead><tr><th>#</th><th>Participante</th><th>Última interacción</th><th>Puntos</th><th>Participación</th><th>Interacciones registradas</th></tr></thead>
                        <tbody>
                            <tr v-if="!filteredLeads.length">
                                <td colspan="6" class="text-center text-muted py-4">No existen asistentes que cumplan los filtros seleccionados durante {{ eventYear }}.</td>
                            </tr>
                            <tr v-for="(lead, index) in filteredLeads" :key="lead.hubspot_negocio_id">
                                <td>{{ ((report.page || 1) - 1) * (report.per_page || 100) + index + 1 }}</td>
                                <td><strong>{{ lead.nombre_negocio || 'Asistente sin nombre' }}</strong><br><small>{{ lead.factores.email || lead.factores.rut || 'Sin contacto informado' }}</small></td>
                                <td>{{ formatEventDate(lead.createdate) }}</td>
                                <td><span class="score-pill" :class="levelClass(lead.nivel)">{{ Number(lead.score_total).toFixed(0) }}</span><br><small>{{ lead.nivel }}</small></td>
                                <td><span class="signal signal-event">{{ lead.eventos_count }} {{ lead.eventos_count === 1 ? 'interacción' : 'interacciones' }} ({{ lead.score_eventos }} pts)</span></td>
                                <td><small>{{ (lead.factores.eventos_nombres || []).join(' · ') || 'Sin detalle' }}</small></td>
                            </tr>
                        </tbody>
                    </table>
                    <table v-else class="table table-hover align-middle scoring-table">
                        <thead><tr><th>#</th><th>Lead</th><th>Programa</th><th>Score (interés)</th><th>Prob. histórica</th><th>Señales</th><th>Responsable</th></tr></thead>
                        <tbody>
                            <tr v-if="!filteredLeads.length">
                                <td colspan="7" class="text-center text-muted py-4">
                                    No hay leads que cumplan los filtros seleccionados.
                                </td>
                            </tr>
                            <tr v-for="(lead, index) in filteredLeads" :key="lead.hubspot_negocio_id">
                                <td>{{ index + 1 }}</td>
                                <td><a v-if="lead.hubspot_url" :href="lead.hubspot_url" target="_blank" rel="noopener"><strong>{{ lead.nombre_negocio || lead.hubspot_negocio_id }}</strong></a><strong v-else>{{ lead.nombre_negocio || lead.hubspot_negocio_id }}</strong><br><small>{{ lead.etapa_actual || 'Sin etapa' }}</small></td>
                                <td>{{ lead.programa || 'Sin programa' }}<br><small>{{ lead.fuente || 'Sin fuente' }}</small></td>
                                <td><span class="score-pill" :class="levelClass(lead.nivel)">{{ lead.score_total.toFixed(1) }}</span><br><small>{{ lead.nivel }}</small></td>
                                <td><strong>{{ lead.probabilidad_historica.toFixed(1) }}%</strong><br><small>según 2025–2026</small></td>
                                <td>
                                    <span class="signal" title="Engagement HubSpot">Web {{ lead.score_engagement }}</span>
                                    <span class="signal" title="Simulaciones locales">Sim {{ lead.simulaciones_count }}</span>
                                    <span class="signal" title="Qué tan reciente es el lead">Reciente {{ lead.score_recencia }}</span>
                                </td>
                                <td>{{ lead.owner || 'Sin asignar' }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="report.last_page > 1" class="d-flex justify-content-center align-items-center mt-3">
                        <button class="btn btn-sm btn-outline-primary mr-3" :disabled="report.page <= 1 || loading" @click="changePage(report.page - 1)">Anterior</button>
                        <span class="text-muted">Página {{ report.page }} de {{ report.last_page }}</span>
                        <button class="btn btn-sm btn-outline-primary ml-3" :disabled="report.page >= report.last_page || loading" @click="changePage(report.page + 1)">Siguiente</button>
                    </div>
                </div>
            </div>
        </div>
        <p class="method-note mt-3" v-if="pregradoMode">El ranking usa eventos y simuladores UGM registrados durante {{ eventYear }}. Cada actividad única suma 1 punto. No evalúa al estudiante ni predice su matrícula.</p>
        <p class="method-note mt-3" v-else>Esta vista usa datos de HubSpot: matrículas históricas 2025–2026, navegación, emails, conversiones, recencia y simulador. No usa datos de eventos de Pregrado.</p>
        </div>
    </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";

export default {
    components: { Layout, PageHeader },
    data() {
        const pregradoMode = window.location.pathname.endsWith('/leadscoringpregrado');

        return {
            title: pregradoMode ? 'Ranking de participación' : 'Priorización de leads',
            items: [
                { text: 'Dashboard', href: '/' },
                { text: pregradoMode ? 'Ranking de participación' : 'Scoring Advance/Postgrado', active: true }
            ],
            report: { leads: [], niveles: {}, page: 1, last_page: 1 },
            loading: false,
            error: '',
            search: '',
            level: '',
            program: '',
            owner: '',
            eventYear: null,
            pregradoMode
        };
    },
    computed: {
        filteredLeads() {
            const needle = this.search.toLowerCase();
            return (this.report.leads || []).filter(lead => {
                const factors = lead.factores || {};
                const haystack = [lead.nombre_negocio, lead.programa, lead.owner, lead.fuente, lead.etapa_actual, factors.email, factors.rut, (factors.eventos_nombres || []).join(' ')].join(' ').toLowerCase();
                return (!needle || haystack.includes(needle))
                    && (!this.level || lead.nivel === this.level)
                    && (!this.program || lead.categoria_programa === this.program)
                    && (!this.owner || (lead.owner || 'Sin asignar') === this.owner);
            });
        },
        programOptions() {
            return ['Advance', 'Postgrado'];
        },
        eventYearOptions() {
            const years = this.report.event_years || [];
            return years.length ? years : (this.eventYear ? [this.eventYear] : []);
        },
        ownerOptions() {
            return this.uniqueSortedOptions((this.report.leads || []).map(lead => lead.owner || 'Sin asignar'));
        },
        cards() {
            const levels = this.report.niveles || {};
            if (this.pregradoMode) {
                return [
                    { label: 'Participantes registrados', value: this.report.total || 0 },
                    { label: 'Ranking promedio', value: Number(this.report.promedio || 0).toFixed(1) },
                    { label: 'Cuatro o más interacciones', value: (levels['Muy alto'] || 0) },
                    { label: 'Tres interacciones', value: (levels.Alto || 0) }
                ];
            }
            return [
                { label: 'Leads calificados', value: this.report.total || 0 },
                { label: 'Score de interés promedio', value: Number(this.report.promedio || 0).toFixed(1) },
                { label: 'Interés muy alto', value: levels['Muy alto'] || 0 },
                { label: 'Interés alto', value: levels.Alto || 0 },
            ];
        },
        formattedDate() {
            return this.report.computed_at ? new Date(this.report.computed_at).toLocaleString('es-CL') : 'pendiente';
        }
    },
    mounted() { this.load(); },
    methods: {
        async load() {
            this.loading = true; this.error = '';
            try { const { data } = await axios.get('/api/dashboard/lead-scoring', { params: { periodo: '2027 - 1', mode: this.pregradoMode ? 'pregrado' : 'advance_postgrado', event_year: this.eventYear, page: this.report.page || 1, limit: this.pregradoMode ? 100 : 500 } }); this.report = data; if (this.pregradoMode && data.event_year) { this.eventYear = data.event_year; } }
            catch (error) { this.error = (error.response && error.response.data && error.response.data.message) || 'No se pudo cargar el piloto.'; }
            finally { this.loading = false; }
        },
        uniqueSortedOptions(values) {
            return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
        },
        changePage(page) {
            this.report.page = page;
            this.load();
        },
        changeEventYear() {
            this.report.page = 1;
            this.search = '';
            this.level = '';
            this.owner = '';
            this.load();
        },
        formatEventDate(value) {
            return value ? new Date(value).toLocaleDateString('es-CL') : 'Sin fecha';
        },
        levelClass(level) { return { 'Muy alto': 'very-high', 'Alto': 'high', 'Medio': 'medium', 'Bajo': 'low' }[level] || 'low'; }
    }
};
</script>

<style scoped>
.pilot-label { color:#556ee6; font-weight:700; letter-spacing:.12em; font-size:.72rem; }
.metric-card { background:linear-gradient(145deg,#fff,#f7f8fc); border:1px solid #edf0f7; border-radius:14px; padding:20px; }
.metric-card small { display:block; color:#74788d; text-transform:uppercase; letter-spacing:.06em; }
.metric-card strong { display:block; font-size:2rem; color:#2a3042; margin-top:8px; }
.scoring-table td { vertical-align:middle; }
.score-pill { display:inline-flex; min-width:58px; justify-content:center; border-radius:999px; color:#fff; font-weight:700; padding:6px 12px; }
.score-pill.very-high { background:#16a085; }.score-pill.high { background:#3498db; }.score-pill.medium { background:#f39c12; }.score-pill.low { background:#95a5a6; }
.signal { display:inline-block; background:#f0f3fa; color:#4b587c; border-radius:6px; padding:4px 7px; margin:2px; font-size:.75rem; }
.signal-event { background:#e8f7f1; color:#167659; }
.empty-state i { font-size:3.5rem; color:#a8b0c3; }.method-note { color:#6c757d; font-size:.82rem; }
</style>
